import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import {
  ReplenishmentScheduleParamsType,
  GetReplenishmentScheduleResponseType,
  GetWarehouseLocationsResponseType,
} from 'features/SupplyOrder/services/replenishment'

const SLICE_NAME = 'REPLENISHMENT_SCHEDULE'

export type OptionsType = {
  name: string
  value: string
}

export type TimeOptionsType = {
  no: number
  name: string
  disabled: boolean
}

export const SCHEDULE_TYPE_DATA = {
  once: {
    name: 'Satu Kali',
    value: 'once',
  },
  recurring: {
    name: 'Berulang',
    value: 'recurring',
  },
  everywhere: {
    name: 'Dimana Saja',
    value: 'everywhere',
  },
}

export type FormScheduleDetailType = {
  id?: number
  daylist: {
    name: string
    value: string
  }[]
  timelist: {
    name: string
    disabled: boolean
  }[]
  categoryIdList: {
    category_id: number
    category_name: string
  }[]
  scheduleType: Nullable<{
    name: string
    value: string
  }>
  location: Nullable<{
    location_id: number
    location_name: string
  }>
  maxSku: string
  startDate: string
  endDate: string
  unlimited: boolean | null
  scheduleOption: 'selectSchedule' | 'now' | ''
  isFlagReplenishV3Active: boolean
}

export type ReplenishmentScheduleFilterType = Partial<
  Omit<ReplenishmentScheduleParamsType, 'pagination.page_size' | 'pagination.page_index'> & {
    page_size: number
    page_index: number
  }
>

export type ReplenishmentScheduleSliceType = {
  isLoading: boolean
  isLoadingConstant: boolean
  filter: ReplenishmentScheduleFilterType
  pagination: GetReplenishmentScheduleResponseType['pagination']
  data: GetReplenishmentScheduleResponseType['data']
  constants: {
    warehouseList: GetWarehouseLocationsResponseType
    categories: {
      category_id: number
      category_name: string
    }[]
    scheduleTypeOptions: OptionsType[]
    scheduleOptions: OptionsType[]
    dayOptions: OptionsType[]
    timeOptions: TimeOptionsType[]
    skuMaxOptions: string[]
  }
  popupAddEditSchedule: {
    isOpen: boolean
    form: FormScheduleDetailType
    errorMessage: {
      maxSku: string
    }
  }
}

const initialState: ReplenishmentScheduleSliceType = {
  isLoading: false,
  isLoadingConstant: false,
  filter: {
    page_size: 20,
    page_index: 1,
  },
  pagination: {
    page_index: 1,
    page_size: 20,
    number_of_elements: 0,
  },
  data: [],
  constants: {
    warehouseList: [],
    categories: [],
    scheduleOptions: [],
    scheduleTypeOptions: [],
    dayOptions: [],
    timeOptions: [],
    skuMaxOptions: [],
  },
  popupAddEditSchedule: {
    isOpen: false,
    form: {
      daylist: [],
      timelist: [],
      categoryIdList: [],
      scheduleType: null,
      location: null,
      maxSku: '',
      startDate: '',
      endDate: '',
      unlimited: null,
      scheduleOption: '',
      isFlagReplenishV3Active: false,
    },
    errorMessage: {
      maxSku: '',
    },
  },
}

const replenishmentScheduleSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    resetReplenishmentSchedule: () => initialState,
    resetReplenishmentScheduleFilter: (state) => {
      state.filter = initialState.filter
    },
    resetPopupAddEditSchedule: (state) => {
      state.popupAddEditSchedule = initialState.popupAddEditSchedule
    },
    setReplenishmentScheduleListData: (
      state,
      action: PayloadAction<GetReplenishmentScheduleResponseType>,
    ) => {
      state.data = action.payload.data
      state.pagination = action.payload.pagination
    },
    setOpenAddSchedulePopup: (state, action: PayloadAction<boolean>) => {
      state.popupAddEditSchedule.isOpen = action.payload
      if (!action.payload) {
        state.popupAddEditSchedule.form = initialState.popupAddEditSchedule.form
        state.popupAddEditSchedule.errorMessage = initialState.popupAddEditSchedule.errorMessage
      }
    },
    setReplenishmentScheduleFilter: (state, action) => {
      state.filter = action.payload
    },
    setAddEditScheduleFormPopup: (
      state,
      action: PayloadAction<{
        key: keyof ReplenishmentScheduleSliceType['popupAddEditSchedule']['form']
        value: unknown
      }>,
    ) => {
      const { key, value } = action.payload

      if (key === 'location') {
        state.popupAddEditSchedule.form.scheduleType = null
      }
      if (['location', 'scheduleType'].includes(key)) {
        state.popupAddEditSchedule.form.scheduleOption = ''
        state.popupAddEditSchedule.form.startDate = ''
        state.popupAddEditSchedule.form.endDate = ''
        state.popupAddEditSchedule.form.daylist = []
        state.popupAddEditSchedule.form.timelist = []
        state.popupAddEditSchedule.form.unlimited = false
        state.popupAddEditSchedule.form.categoryIdList = []
        state.popupAddEditSchedule.form.maxSku = ''
      }

      if (key === 'unlimited') {
        state.popupAddEditSchedule.form.endDate = ''
      }

      state.popupAddEditSchedule.form[key] = value as never
    },
    setConstants: (state, action) => {
      state.constants = {
        ...state.constants,
        ...action.payload,
      }
    },
    setPopupAddEditFormOnFetchDetailSucceed: (state, action) => {
      state.popupAddEditSchedule.form = action.payload
    },
    setIsLoadingConstant: (state, action) => {
      state.isLoadingConstant = action.payload
    },
    setPopupAddEditScheduleErrorMessage: (
      state,
      action: PayloadAction<
        Partial<ReplenishmentScheduleSliceType['popupAddEditSchedule']['errorMessage']>
      >,
    ) => {
      state.popupAddEditSchedule.errorMessage = {
        ...state.popupAddEditSchedule.errorMessage,
        ...action.payload,
      }
    },
  },
})

export const {
  setIsLoading,
  resetReplenishmentSchedule,
  resetReplenishmentScheduleFilter,
  resetPopupAddEditSchedule,
  setReplenishmentScheduleListData,
  setOpenAddSchedulePopup,
  setReplenishmentScheduleFilter,
  setAddEditScheduleFormPopup,
  setConstants,
  setPopupAddEditFormOnFetchDetailSucceed,
  setIsLoadingConstant,
  setPopupAddEditScheduleErrorMessage,
} = replenishmentScheduleSlice.actions
export default replenishmentScheduleSlice.reducer
